import React, { useCallback, useEffect, useState } from 'react';
import { Box, Dialog, IconButton, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import KeyboardArrowLeft from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRight from '@mui/icons-material/KeyboardArrowRight';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { IPortalHomeSectionItemResponse } from '../../../model/base/response/portal-home-response';
import { useMenuLinkNavigation } from '../../hooks/useMenuLinkNavigation';

/**
 * Layout da Galeria de Imagens, gravado pelo APM em tblSecaoHome.DesConfiguracao (JSON).
 * Ex.: {"layout":"carousel","columns":4}. Sem configuração (ou inválida): grade de 3 colunas.
 */
interface GalleryConfiguration {
  layout?: 'grid' | 'carousel';
  columns?: number;
}

const parseConfiguration = (json?: string): Required<GalleryConfiguration> => {
  let config: GalleryConfiguration = {};
  try {
    config = json ? JSON.parse(json) : {};
  } catch {
    config = {};
  }
  const columns = Math.min(6, Math.max(2, Math.round(Number(config?.columns) || 3)));
  return { layout: config?.layout === 'carousel' ? 'carousel' : 'grid', columns };
};

interface ImageGalleryProps {
  items: IPortalHomeSectionItemResponse[];
  configuration?: string;
}

/**
 * Galeria de Imagens da home customizada: grade ou carrossel com legenda opcional.
 * Imagem com link abre o destino; sem link, amplia em tela cheia com navegação entre as imagens.
 */
export default function ImageGallery({ items, configuration }: ImageGalleryProps) {
  const openLink = useMenuLinkNavigation();
  const [viewing, setViewing] = useState<number | null>(null);
  const images = items.filter((item) => !!item.imageUrl);
  const { layout, columns } = parseConfiguration(configuration);

  if (!images.length) return null;

  const handleOpen = (index: number) => {
    const item = images[index];
    if (item.url) openLink(item.url, item.openInNewTab);
    else setViewing(index);
  };

  const tile = (item: IPortalHomeSectionItemResponse, index: number) => (
    <Box component="figure" key={item.id} sx={{ m: 0, minWidth: 0 }}>
      <Box
        component="button"
        type="button"
        onClick={() => handleOpen(index)}
        aria-label={item.url ? `${item.title || 'Imagem'}: abrir link` : `Ampliar ${item.title || 'imagem'}`}
        sx={{
          position: 'relative',
          display: 'block',
          width: '100%',
          p: 0,
          border: 0,
          borderRadius: '12px',
          overflow: 'hidden',
          cursor: 'pointer',
          aspectRatio: '4 / 3',
          backgroundColor: 'grey.100',
          '& img': { transition: 'transform 300ms cubic-bezier(0.22, 1, 0.36, 1)' },
          '&:hover img, &:focus-visible img': { transform: 'scale(1.04)' },
          '&:hover .gallery-hint, &:focus-visible .gallery-hint': { opacity: 1 },
        }}
      >
        <Box
          component="img"
          src={item.imageUrl}
          alt={item.title || ''}
          loading="lazy"
          sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
        />
        <Box
          className="gallery-hint"
          aria-hidden
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            display: 'inline-flex',
            p: 0.75,
            borderRadius: '50%',
            color: 'common.white',
            backgroundColor: 'rgba(0, 0, 0, 0.55)',
            opacity: 0,
            transition: 'opacity 200ms',
            '& .MuiSvgIcon-root': { fontSize: '1.125rem' },
          }}
        >
          {item.url ? <OpenInNewIcon /> : <ZoomInIcon />}
        </Box>
      </Box>
      {item.title && (
        <Typography component="figcaption" variant="body2" color="text.secondary" sx={{ mt: 1, lineHeight: 1.4 }}>
          {item.title}
        </Typography>
      )}
    </Box>
  );

  return (
    <>
      {layout === 'carousel' ? (
        // Carrossel com rolagem nativa (toque, trackpad e teclado) e encaixe por imagem
        <Box
          sx={{
            display: 'grid',
            gridAutoFlow: 'column',
            gridAutoColumns: {
              xs: '80%',
              sm: 'calc((100% - 16px) / 2)',
              md: `calc((100% - ${(columns - 1) * 24}px) / ${columns})`,
            },
            gap: { xs: 2, md: 3 },
            overflowX: 'auto',
            scrollSnapType: 'x mandatory',
            pb: 1,
            '& > *': { scrollSnapAlign: 'start' },
          }}
        >
          {images.map(tile)}
        </Box>
      ) : (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: 'repeat(2, minmax(0, 1fr))',
              sm: `repeat(${Math.min(columns, 3)}, minmax(0, 1fr))`,
              md: `repeat(${columns}, minmax(0, 1fr))`,
            },
            gap: { xs: 2, md: 3 },
          }}
        >
          {images.map(tile)}
        </Box>
      )}

      <GalleryViewer images={images} index={viewing} onChange={setViewing} />
    </>
  );
}

interface GalleryViewerProps {
  images: IPortalHomeSectionItemResponse[];
  index: number | null;
  onChange: (index: number | null) => void;
}

function GalleryViewer({ images, index, onChange }: GalleryViewerProps) {
  const total = images.length;
  const open = index !== null;
  const go = useCallback(
    (step: number) => index !== null && onChange((index + step + total) % total),
    [index, onChange, total]
  );

  useEffect(() => {
    if (!open || total < 2) return;
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === 'ArrowLeft') go(-1);
      if (event.key === 'ArrowRight') go(1);
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [open, total, go]);

  const item = index !== null ? images[index] : undefined;
  const navSx = {
    color: 'common.white',
    backgroundColor: 'rgba(255, 255, 255, 0.12)',
    width: 48,
    height: 48,
    '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.24)' },
  };

  return (
    <Dialog
      open={open}
      onClose={() => onChange(null)}
      fullScreen
      aria-label={item?.title || 'Imagem ampliada'}
      PaperProps={{ sx: { backgroundColor: 'rgba(0, 0, 0, 0.92)', color: 'common.white' } }}
    >
      {item && (
        <Box sx={{ position: 'relative', height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', p: { xs: 2, md: 6 } }}>
          <IconButton onClick={() => onChange(null)} aria-label="fechar" sx={{ ...navSx, position: 'absolute', top: 16, right: 16 }}>
            <CloseIcon />
          </IconButton>

          <Box
            component="img"
            src={item.imageUrl}
            alt={item.title || ''}
            sx={{ maxWidth: '100%', maxHeight: 'calc(100% - 64px)', objectFit: 'contain', borderRadius: 1 }}
          />

          <Typography sx={{ mt: 2, textAlign: 'center', color: 'rgba(255, 255, 255, 0.85)' }}>
            {item.title}
            {total > 1 && (
              <Box component="span" sx={{ ml: item.title ? 1.5 : 0, opacity: 0.7, fontVariantNumeric: 'tabular-nums' }}>
                {index! + 1} / {total}
              </Box>
            )}
          </Typography>

          {total > 1 && (
            <>
              <IconButton onClick={() => go(-1)} aria-label="imagem anterior" sx={{ ...navSx, position: 'absolute', left: { xs: 8, md: 24 }, top: '50%', transform: 'translateY(-50%)' }}>
                <KeyboardArrowLeft />
              </IconButton>
              <IconButton onClick={() => go(1)} aria-label="próxima imagem" sx={{ ...navSx, position: 'absolute', right: { xs: 8, md: 24 }, top: '50%', transform: 'translateY(-50%)' }}>
                <KeyboardArrowRight />
              </IconButton>
            </>
          )}
        </Box>
      )}
    </Dialog>
  );
}
